import { useCallback } from 'react';
import { SWEventType, SWStateEvent, InitializationFailedEvent, LogementsSyncFailedEvent } from '../service-worker/SWEventTypes';
import { trackException, trackEvent } from '../services/AppInsightsPlugin';

/**
 * Hook to track Service Worker events in Application Insights
 *
 * Handles:
 * - UI state updates (LOADING, SYNCING, READY, UNAVAILABLE, SW_UPDATING)
 * - Telemetry events (INITIALIZATION_*, LOGEMENTS_SYNC_*, DOCUMENTS_SYNC_*)
 * - SW errors and exceptions
 * - SW lifecycle events (ACTIVATED_ON_LOAD, COLD_START)
 */
export const useEventTracking = () => {
    const trackSWStateEvent = useCallback((event: SWStateEvent) => {
        // Track UI state events
        if (event.type === SWEventType.LOADING) {
            trackEvent('SW_LOADING');
        } else if (event.type === SWEventType.SYNCING) {
            trackEvent('SW_SYNCING');
        } else if (event.type === SWEventType.READY) {
            trackEvent('SW_READY');
        } else if (event.type === SWEventType.UNAVAILABLE) {
            trackEvent('SW_UNAVAILABLE');
        } else if (event.type === SWEventType.SW_UPDATING) {
            trackEvent('SW_UPDATING');
        } else if (event.type === SWEventType.INITIALIZATION_STARTED) {
            trackEvent('SW_INITIALIZATION_STARTED');
        } else if (event.type === SWEventType.INITIALIZATION_COMPLETED) {
            trackEvent('SW_INITIALIZATION_COMPLETED');
        } else if (event.type === SWEventType.INITIALIZATION_FAILED) {
            const failedEvent = event as InitializationFailedEvent;
            trackEvent('SW_INITIALIZATION_FAILED', {
                reason: failedEvent.details?.reason,
                error: failedEvent.details?.error
            });
        } else if (event.type === SWEventType.CLEAR_STARTED) {
            trackEvent('SW_CLEAR_STARTED');
        } else if (event.type === SWEventType.CLEAR_COMPLETED) {
            trackEvent('SW_CLEAR_COMPLETED');
        } else if (event.type === SWEventType.LOGEMENTS_SYNC_STARTED) {
            trackEvent('SW_LOGEMENTS_SYNC_STARTED', {
                syncType: 'full',
                triggerReason: 'auto-sync'
            });
        } else if (event.type === SWEventType.LOGEMENTS_SYNC_COMPLETED) {
            trackEvent('SW_LOGEMENTS_SYNC_COMPLETED', {
                logementCount: undefined
            });
        } else if (event.type === SWEventType.LOGEMENTS_SYNC_FAILED) {
            const failedEvent = event as LogementsSyncFailedEvent;
            if (failedEvent.details?.reason) {
                // Abort attendu (UNAUTHORIZED, CONTEXT_CHANGED, etc.) : trackEvent simple
                trackEvent('SW_LOGEMENTS_SYNC_FAILED', {
                    reason: failedEvent.details.reason
                });
            } else {
                // Erreur inattendue : trackException avec stack pour diagnostic
                const syncError = new Error(failedEvent.details?.error ?? 'Unknown sync error');
                if (failedEvent.details?.stack) {
                    syncError.stack = failedEvent.details.stack;
                }
                trackException(syncError, { type: 'syncLogementsFailed' });
            }
        } else if (event.type === SWEventType.DOCUMENTS_SYNC_PROGRESS) {
            trackEvent('SW_DOCUMENTS_SYNC_PROGRESS');
        } else if (event.type === SWEventType.DOCUMENTS_SYNC_COMPLETED) {
            trackEvent('SW_DOCUMENTS_SYNC_COMPLETED');
        }
    }, []);

    const trackSWError = useCallback((message: string, filename: string, lineno: number, colno: number, stack: string) => {
        const swError = new Error(`Service Worker Error: ${message}`);
        swError.stack = stack;
        trackException(swError, {
            type: 'swError',
            filename,
            lineno: String(lineno),
            colno: String(colno)
        });
    }, []);

    const trackUnhandledRejection = useCallback((reason: string, stack: string) => {
        const rejectionError = new Error(`Service Worker Unhandled Promise Rejection: ${reason}`);
        rejectionError.stack = stack;
        trackException(rejectionError, {
            type: 'swUnhandledRejection'
        });
    }, []);

    const trackActivatedOnLoad = useCallback(() => {
        trackEvent('SW_ACTIVATED_ON_LOAD');
    }, []);

    const trackColdStart = useCallback(() => {
        trackEvent('SW_COLD_START');
    }, []);

    return {
        trackSWStateEvent,
        trackSWError,
        trackUnhandledRejection,
        trackActivatedOnLoad,
        trackColdStart
    };
};
